"use client";
import { Track } from "@/src/lib/tracking";

import React, { useEffect, useState } from "react";
import { cn, dayjs } from "utils";

import { AgentReportLevelEnum } from "@features/api/memberApi";
import { I18nUtil } from "@utils/i18n";
import { OspIcons } from "@ospweb/sdk-ui/src/Icon/OspIcons";
import Translations from "@translate/components/Notification/Notification";
import { useNotificationContext } from "./NotificationContext";
import { useShellRouter } from "@hooks/useShellAwareRouter";
import { useSwipeDismiss } from "./hooks/useSwipeDismiss";

const I18n = I18nUtil.createScoped(Translations);

/**
 * 全局通知组件
 * 显示在页面顶部的通知卡片，支持自动关闭和点击跳转
 */
export const Notification: React.FC = () => {
  const { currentNotification, closeNotification, setIsHovering } =
    useNotificationContext();
  const [isVisible, setIsVisible] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const router = useShellRouter();

  const {
    cardRef,
    isDragging,
    shouldPreventClickRef,
    touchAction,
    contentStyle,
    handlers,
  } = useSwipeDismiss({
    onDismiss: closeNotification,
  });

  useEffect(() => {
    if (currentNotification) {
      // 如果已经有通知在显示，先触发刷新动画
      if (isVisible) {
        setIsRefreshing(true);
        setTimeout(() => {
          setIsRefreshing(false);
          setIsVisible(true);
        }, 300);
      } else {
        // 首次显示
        setIsVisible(true);
      }
    } else {
      setIsVisible(false);
      setIsRefreshing(false);
    }
  }, [currentNotification, isVisible]);

  /**
   * 处理通知点击
   */
  const handleClick = () => {
    if (shouldPreventClickRef.current) {
      shouldPreventClickRef.current = false;
      return;
    }

    if (currentNotification?.onClick) {
      currentNotification.onClick();
    }
    if (currentNotification?.link) {
      router.push(currentNotification.link);
    }
    Track.event("banner_notification_tap");
    closeNotification();
  };

  /**
   * 处理关闭按钮点击
   */
  const handleClose = (e: React.MouseEvent) => {
    e.stopPropagation();
    closeNotification();
  };

  /**
   * 处理鼠标进入通知区域
   */
  const handleMouseEnter = () => {
    setIsHovering(true);
  };

  /**
   * 处理鼠标离开通知区域
   */
  const handleMouseLeave = () => {
    setIsHovering(false);
  };

  if (!currentNotification || !isVisible) {
    return null;
  }

  const hasAction = !!(currentNotification.onClick || currentNotification.link);

  return (
    <div className="fixed top-10 right-10 z-[9999] w-[400px] max-w-[calc(100vw-5rem)]">
      <div
        ref={cardRef}
        data-openreplay-label="notification_notification_click"
        className={cn(
          "relative rounded-2xl p-4 shadow-lg border-[1px]",
          "bg-background_solid_quaternary border border-border_white_transparent10",
          "transition-all duration-300 ease-in-out",
          isRefreshing && "opacity-0 scale-95",
          !isRefreshing && "opacity-100 scale-100 animate-slide-down-right",
          hasAction && "cursor-pointer",
          isDragging && "cursor-grabbing",
        )}
        style={{ touchAction }}
        onClick={hasAction ? handleClick : undefined}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onPointerDown={handlers.onPointerDown}
        onPointerMove={handlers.onPointerMove}
        onPointerUp={handlers.onPointerUp}
        onPointerCancel={handlers.onPointerCancel}
        data-tracking-eventname="banner_notification_view"
      >
        {/* Content drag layer keeps parent animation untouched. */}
        <div style={contentStyle}>
          {/* 内容区域 */}
          <div className="flex items-start">
            {/* 图标 */}

            {/* 文本内容 */}
            <div className="flex-1 min-w-0">
              {/* header */}
              <div className="flex items-center gap-2 mb-1">
                {/* <span className="title_l text-content_primary">
                {currentNotification.extParams?.agentName}
              </span> */}
                <div className="flex-shrink-0 w-8 h-8 bg-background_white_overlay10 rounded-2 flex items-center justify-center mt-0.5">
                  <OspIcons
                    name="bell"
                    size={20}
                    className="text-content_primary"
                  />
                </div>
                <div className="flex md:items-center md:gap-[10px] gap-1 md:flex-row flex-col">
                  <div className="title_l text-content_primary">
                    {`${currentNotification.extParams?.agentName} `}
                    {I18n.t("Alert")}
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="flex items-center gap-1">
                      <span
                        className={`w-[6px] h-[6px] rounded-full bg-content_accent3_primary`}
                      />
                      <span className="body_medium_s text-content_secondary">
                        {currentNotification.level === AgentReportLevelEnum.HIGH
                          ? I18n.t("Critical")
                          : currentNotification.level ===
                              AgentReportLevelEnum.MID
                            ? I18n.t("Important")
                            : ""}
                      </span>
                    </div>
                    <div className="flex items-center gap-1 w-1 h-1 rounded-full bg-background_white_overlay20" />
                    <div className="body_s text-content_tertiary">
                      {(() => {
                        const notificationTime = dayjs(
                          currentNotification.time,
                        );
                        const isToday = notificationTime.isSame(dayjs(), "day");
                        const timeStr = notificationTime.format("h:mmA");
                        return isToday
                          ? `${I18n.t("Today")} ${timeStr}`
                          : timeStr;
                      })()}
                    </div>
                  </div>
                </div>

                {/* {currentNotification.important && (
                <span className="w-1.5 h-1.5 rounded-full bg-content_accent2_primary flex-shrink-0" />
              )} */}
                {/* {currentNotification.time && (
                <span className="body_s text-content_tertiary flex-shrink-0">
                  {currentNotification.time}
                </span>
              )} */}
              </div>

              {/* 消息内容 */}
              <p className="body_m text-content_secondary line-clamp-2 mt-2">
                {currentNotification.title}
              </p>

              {/* Details按钮 */}
              {hasAction && (
                <button
                  aria-label="Notification"
                  data-openreplay-label="notification_notification_click"
                  id="btn-notification"
                  className={cn(
                    "mt-4 w-20 h-7 rounded-[6px] justify-self-end",
                    "bg-background_white_overlay100 button xs_button",
                    "button_s text-content_inverse_primary",
                    // "transition-colors duration-200",
                  )}
                  onClick={handleClick}
                >
                  {I18n.t("Details")}
                </button>
              )}
            </div>

            {/* 关闭按钮 */}
            <button
              data-openreplay-label="notification_closenotification_close"
              id="btn-close-notification"
              className={cn(
                "absolute top-[14px] right-[14px] flex-shrink-0 w-6 h-6 rounded-full",
                "flex items-center justify-center",
                "hover:bg-background_white_overlay10",
                "transition-colors duration-200",
              )}
              onClick={handleClose}
              aria-label="Close notification"
            >
              <OspIcons
                name="close"
                size={16}
                className="text-content_tertiary"
              />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
